import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Formik, Form, FormikHelpers } from 'formik';
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { colors } from '../../theme/theme';
import { useCategory } from '../../hooks/useCategory';
import { useBusiness, Business, UpdateBusinessRequest } from '../../hooks/useBusiness';
import { useAuthService } from '../../hooks/useAuthService';
import { COUNTRIES, DEFAULT_COUNTRY, getCountryByName } from '../../constants/countries';
import { getStatesByCountryId, getStateByName } from '../../constants/statesData';
import { geocodeAddress, buildFullAddress, areAddressesSame, AddressComponents } from '../../utils/geocoding';
import {
  addBusinessSchema,
  initialValues,
  AddBusinessFormValues,
} from './schemas/addBusinessSchema';

const OTHER_CATEGORY_NAME = 'Other';

const EditBusinessScreen: React.FC = () => {
  const navigate = useNavigate();

  const { categories, isLoading: isCategoriesLoading } = useCategory();
  const { updateBusiness, getMyBusiness, isLoading: isUpdating } = useBusiness();
  const { getUserProfile } = useAuthService();

  // State for loading business data
  const [isLoadingBusiness, setIsLoadingBusiness] = useState(true);
  const [business, setBusiness] = useState<Business | null>(null);
  const [userDisplayName, setUserDisplayName] = useState<string>('');

  // UI state
  const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<'success' | 'error'>('error');
  const [isOtherCategoryMode, setIsOtherCategoryMode] = React.useState<boolean>(false);

  // Fetch business data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [businessData, profileData] = await Promise.all([
          getMyBusiness(),
          getUserProfile(),
        ]);

        if (!businessData) {
          navigate('/search-business', { replace: true });
          return;
        }

        setBusiness(businessData);
        setUserDisplayName(profileData?.data?.display_name || '');

        // Check if current category is "Other"
        const otherCat = categories.find((cat) => cat.name.toLowerCase() === OTHER_CATEGORY_NAME.toLowerCase());
        if (otherCat && businessData.category_id === otherCat.id) {
          setIsOtherCategoryMode(true);
        }
      } catch (err) {
        console.error('Failed to fetch business:', err);
        setSnackbarMessage('Failed to load business details');
        setSnackbarSeverity('error');
        setShowSnackbar(true);
      } finally {
        setIsLoadingBusiness(false);
      }
    };

    fetchData();
  }, [getMyBusiness, getUserProfile, navigate, categories]);

  // Find the "Other" category
  const otherCategory = useMemo(() => {
    return categories.find((cat) => cat.name.toLowerCase() === OTHER_CATEGORY_NAME.toLowerCase());
  }, [categories]);

  // Filter out "Other" category for dropdown
  const categoryOptions = useMemo(() => {
    return categories.filter(
      (cat) => cat.name.toLowerCase() !== OTHER_CATEGORY_NAME.toLowerCase()
    );
  }, [categories]);

  // Create initial form values from business data
  const formInitialValues = useMemo((): AddBusinessFormValues => {
    if (!business) {
      return initialValues;
    }

    // Get country ID from country name
    const country = business.country_name ? getCountryByName(business.country_name) : null;
    const countryId = country ? String(country.id) : '';
    const phoneCountryCode = country ? country.iso2 : 'IN';
    const phoneDialCode = country ? country.dialCode : '+91';

    // Get state ID from state name
    const stateData = country && business.state_name
      ? getStateByName(country.id, business.state_name)
      : null;
    const stateId = stateData ? String(stateData.id) : '';

    // Get billing country ID
    const billingCountry = business.billing_country_name ? getCountryByName(business.billing_country_name) : null;
    const billingCountryId = billingCountry ? String(billingCountry.id) : '';
    const billingPhoneCountryCode = billingCountry ? billingCountry.iso2 : 'IN';
    const billingPhoneDialCode = billingCountry ? billingCountry.dialCode : '+91';

    // Get billing state ID
    const billingStateData = billingCountry && business.billing_state_name
      ? getStateByName(billingCountry.id, business.billing_state_name)
      : null;
    const billingStateId = billingStateData ? String(billingStateData.id) : '';

    return {
      fullName: userDisplayName || '',
      businessName: business.name || '',
      businessCategory: business.category_id || '',
      otherCategoryName: business.other_category_name || '',
      businessEmail: business.email || '',
      phoneNumber: business.phone_number || '',
      phoneCountryCode,
      phoneDialCode,
      phoneLocale: 'en-IN',
      website: '',
      streetAddress: business.street_address || '',
      city: business.city || '',
      state: stateId,
      stateName: business.state_name || '',
      postalCode: business.postal_code || '',
      country: countryId,
      countryName: business.country_name || '',
      latitude: business.latitude,
      longitude: business.longitude,
      sameAsBusiness: business.same_as_business || false,
      registeredBusinessName: business.billing_name || '',
      gstNumber: business.gstin || '',
      billingEmail: business.billing_email || '',
      billingPhoneNumber: business.billing_phone || '',
      billingPhoneCountryCode,
      billingPhoneDialCode,
      billingPhoneLocale: 'en-IN',
      billingStreetAddress: business.billing_street_address || '',
      billingCity: business.billing_city || '',
      billingState: billingStateId,
      billingStateName: business.billing_state_name || '',
      billingPostalCode: business.billing_postal_code || '',
      billingCountry: billingCountryId,
      billingCountryName: business.billing_country_name || '',
      billingLatitude: business.billing_latitude,
      billingLongitude: business.billing_longitude,
    };
  }, [business, userDisplayName]);

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  // Handle form submission
  const handleSubmit = useCallback(
    async (
      values: AddBusinessFormValues,
      { setFieldValue }: FormikHelpers<AddBusinessFormValues>
    ): Promise<void> => {
      try {
        // Build business address for geocoding
        const businessAddress: AddressComponents = {
          streetAddress: values.streetAddress,
          city: values.city,
          state: values.stateName,
          country: values.countryName,
          postalCode: values.postalCode,
        };

        // Build billing address for geocoding
        const billingAddressComponents: AddressComponents = values.sameAsBusiness
          ? businessAddress
          : {
              streetAddress: values.billingStreetAddress,
              city: values.billingCity,
              state: values.billingStateName,
              country: values.billingCountryName,
              postalCode: values.billingPostalCode,
            };

        let businessLat = values.latitude;
        let businessLng = values.longitude;
        let billingLat = values.billingLatitude;
        let billingLng = values.billingLongitude;

        // Check if business address has changed from original - force re-geocode if so
        if (business) {
          const originalBusinessAddress: AddressComponents = {
            streetAddress: business.street_address || '',
            city: business.city || '',
            state: business.state_name || '',
            country: business.country_name || '',
            postalCode: business.postal_code || '',
          };
          if (!areAddressesSame(businessAddress, originalBusinessAddress)) {
            businessLat = null;
            businessLng = null;
          }

          const originalBillingAddress: AddressComponents = {
            streetAddress: business.billing_street_address || '',
            city: business.billing_city || '',
            state: business.billing_state_name || '',
            country: business.billing_country_name || '',
            postalCode: business.billing_postal_code || '',
          };
          if (!areAddressesSame(billingAddressComponents, originalBillingAddress)) {
            billingLat = null;
            billingLng = null;
          }
        }

        // Geocode business address if lat/long not available
        if (!businessLat || !businessLng) {
          const fullBusinessAddress = buildFullAddress(businessAddress);
          const businessGeocode = await geocodeAddress(fullBusinessAddress);

          if (!businessGeocode) {
            setSnackbarMessage("We can't find your business location. Please add a valid address.");
            setSnackbarSeverity('error');
            setShowSnackbar(true);
            return;
          }

          businessLat = businessGeocode.latitude;
          businessLng = businessGeocode.longitude;
          setFieldValue('latitude', businessLat);
          setFieldValue('longitude', businessLng);
        }

        // Geocode billing address
        const addressesAreSame = areAddressesSame(businessAddress, billingAddressComponents);
        if (!billingLat || !billingLng) {
          if (addressesAreSame) {
            billingLat = businessLat;
            billingLng = businessLng;
          } else {
            const fullBillingAddress = buildFullAddress(billingAddressComponents);
            const billingGeocode = await geocodeAddress(fullBillingAddress);

            if (!billingGeocode) {
              setSnackbarMessage("We can't find your billing location. Please add a valid address.");
              setSnackbarSeverity('error');
              setShowSnackbar(true);
              return;
            }

            billingLat = billingGeocode.latitude;
            billingLng = billingGeocode.longitude;
          }
          setFieldValue('billingLatitude', billingLat);
          setFieldValue('billingLongitude', billingLng);
        }

        const requestData: UpdateBusinessRequest = {
          full_name: values.fullName,
          business_name: values.businessName,
          business_category: values.businessCategory,
          business_email: values.businessEmail,
          phone_number: values.phoneNumber,
          phone_country_code: values.phoneCountryCode,
          phone_dial_code: values.phoneDialCode,
          street_address: values.streetAddress,
          city: values.city,
          state_name: values.stateName,
          postal_code: values.postalCode,
          country_name: values.countryName,
          gst_number: values.gstNumber || '',
          same_as_business: values.sameAsBusiness,
          latitude: businessLat,
          longitude: businessLng,
          ...(isOtherCategoryMode && values.otherCategoryName
            ? { other_category_name: values.otherCategoryName }
            : {}),
        };

        // Add billing details
        requestData.registered_business_name = values.sameAsBusiness
          ? values.businessName
          : values.registeredBusinessName;
        requestData.billing_email = values.sameAsBusiness ? values.businessEmail : values.billingEmail;
        requestData.billing_phone_number = values.sameAsBusiness
          ? values.phoneNumber
          : values.billingPhoneNumber;
        requestData.billing_phone_country_code = values.sameAsBusiness
          ? values.phoneCountryCode
          : values.billingPhoneCountryCode;
        requestData.billing_phone_dial_code = values.sameAsBusiness
          ? values.phoneDialCode
          : values.billingPhoneDialCode;
        requestData.billing_street_address = values.sameAsBusiness
          ? values.streetAddress
          : values.billingStreetAddress;
        requestData.billing_city = values.sameAsBusiness ? values.city : values.billingCity;
        requestData.billing_state_name = values.sameAsBusiness
          ? values.stateName
          : values.billingStateName;
        requestData.billing_postal_code = values.sameAsBusiness
          ? values.postalCode
          : values.billingPostalCode;
        requestData.billing_country_name = values.sameAsBusiness
          ? values.countryName
          : values.billingCountryName;
        requestData.billing_latitude = billingLat;
        requestData.billing_longitude = billingLng;

        await updateBusiness(requestData);

        setSnackbarMessage('Business updated successfully!');
        setSnackbarSeverity('success');
        setShowSnackbar(true);

        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message || 'Failed to update business';
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity('error');
        setShowSnackbar(true);
      }
    },
    [updateBusiness, navigate, isOtherCategoryMode, business]
  );

  if (isLoadingBusiness) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.backgroundDefault,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: colors.backgroundDefault,
        pb: 4,
      }}
    >
      {/* Back to Pricing Link */}
      <Box sx={{ maxWidth: 1024, mx: 'auto', pt: 4, px: 2 }}>
        <Link
          to="/pricing"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: colors.textSecondary,
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: 14,
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} />
          Back to Pricing
        </Link>
      </Box>

      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mt: 2, mb: 4, maxWidth: 1024, mx: 'auto', px: 2 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 2,
            background: `linear-gradient(180deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <BusinessIcon sx={{ color: colors.textWhite, fontSize: 28 }} />
        </Box>

        <Typography
          sx={{
            color: colors.textPrimary,
            fontWeight: 700,
            fontSize: 36,
            lineHeight: '40px',
            mb: 1,
          }}
        >
          Edit Business Details
        </Typography>

        <Typography
          sx={{
            color: colors.textSecondary,
            fontSize: 16,
            lineHeight: '24px',
          }}
        >
          Update your business information
        </Typography>
      </Box>

      {/* Form Card */}
      <Card
        sx={{
          maxWidth: 1024,
          mx: 'auto',
          p: 5,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Formik
          initialValues={formInitialValues}
          validationSchema={addBusinessSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
            submitCount,
          }) => {
            // Get available states based on selected country
            const availableStates = values.country
              ? getStatesByCountryId(Number(values.country))
              : [];

            const billingAvailableStates = values.billingCountry
              ? getStatesByCountryId(Number(values.billingCountry))
              : [];

            // Helper to get error message - show errors after submit attempt or if field was touched
            const getError = (field: keyof AddBusinessFormValues): string | undefined => {
              const hasError = errors[field];
              const wasTouched = touched[field];
              const wasSubmitted = submitCount > 0;
              return (wasTouched || wasSubmitted) && hasError ? (hasError as string) : undefined;
            };

            // Handle "Add Other" click
            const handleAddOther = () => {
              if (otherCategory) {
                setFieldValue('businessCategory', otherCategory.id);
                setFieldValue('otherCategoryName', '');
                setIsOtherCategoryMode(true);
              }
            };

            // Handle reset other category
            const handleResetOtherCategory = () => {
              setFieldValue('businessCategory', '');
              setFieldValue('otherCategoryName', '');
              setIsOtherCategoryMode(false);
            };

            // Handle country change
            const handleCountryChange = (countryId: number) => {
              const country = COUNTRIES.find((c) => c.id === countryId);
              if (country) {
                setFieldValue('country', String(country.id));
                setFieldValue('countryName', country.name);
                setFieldValue('state', '');
                setFieldValue('stateName', '');
              }
            };

            // Handle billing country change
            const handleBillingCountryChange = (countryId: number) => {
              const country = COUNTRIES.find((c) => c.id === countryId);
              if (country) {
                setFieldValue('billingCountry', String(country.id));
                setFieldValue('billingCountryName', country.name);
                setFieldValue('billingState', '');
                setFieldValue('billingStateName', '');
              }
            };

            // Handle state change
            const handleStateChange = (stateId: number) => {
              const stateData = availableStates.find((s) => s.id === stateId);
              if (stateData) {
                setFieldValue('state', String(stateData.id));
                setFieldValue('stateName', stateData.name);
              }
            };

            // Handle billing state change
            const handleBillingStateChange = (stateId: number) => {
              const stateData = billingAvailableStates.find((s) => s.id === stateId);
              if (stateData) {
                setFieldValue('billingState', String(stateData.id));
                setFieldValue('billingStateName', stateData.name);
              }
            };

            // Handle phone country change
            const handlePhoneCountryChange = (dialCode: string) => {
              const country = COUNTRIES.find((c) => c.dialCode === dialCode);
              if (country) {
                setFieldValue('phoneCountryCode', country.iso2);
                setFieldValue('phoneDialCode', country.dialCode);
                setFieldValue('phoneLocale', country.locale);
              }
            };

            // Handle billing phone country change
            const handleBillingPhoneCountryChange = (dialCode: string) => {
              const country = COUNTRIES.find((c) => c.dialCode === dialCode);
              if (country) {
                setFieldValue('billingPhoneCountryCode', country.iso2);
                setFieldValue('billingPhoneDialCode', country.dialCode);
                setFieldValue('billingPhoneLocale', country.locale);
              }
            };

            // Get selected country for phone display
            const selectedPhoneCountry =
              COUNTRIES.find((c) => c.iso2 === values.phoneCountryCode) || DEFAULT_COUNTRY;
            const selectedBillingPhoneCountry =
              COUNTRIES.find((c) => c.iso2 === values.billingPhoneCountryCode) || DEFAULT_COUNTRY;

            return (
              <Form>
                {/* Owner Information Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PersonOutlineIcon sx={{ color: colors.textPrimary, fontSize: 20 }} />
                    <Typography sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: 18 }}>
                      Owner Information
                    </Typography>
                  </Box>

                  <Box>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      Full Name <span style={{ color: colors.error }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="fullName"
                      value={values.fullName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your full name"
                      error={!!getError('fullName')}
                      helperText={getError('fullName')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputStyles}
                    />
                  </Box>
                </Box>

                {/* Business Information Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <BusinessIcon sx={{ color: colors.textPrimary, fontSize: 20 }} />
                    <Typography sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: 18 }}>
                      Business Information
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Business Name */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Business Name <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="businessName"
                        value={values.businessName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter business name"
                        error={!!getError('businessName')}
                        helperText={getError('businessName')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Box>

                    {/* Business Category */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Business Category <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      {isOtherCategoryMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <TextField
                            fullWidth
                            name="otherCategoryName"
                            value={values.otherCategoryName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter your category name"
                            error={!!getError('otherCategoryName')}
                            helperText={getError('otherCategoryName')}
                            sx={inputStyles}
                          />
                          <Button
                            onClick={handleResetOtherCategory}
                            sx={{
                              minWidth: 56,
                              height: 56,
                              borderRadius: '14px',
                              border: `1px solid ${colors.inputBorder}`,
                              color: colors.textSecondary,
                              '&:hover': {
                                borderColor: colors.error,
                                color: colors.error,
                              },
                            }}
                          >
                            <CloseIcon />
                          </Button>
                        </Box>
                      ) : (
                        <FormControl fullWidth error={!!getError('businessCategory')} sx={formControlStyles}>
                          <Select
                            name="businessCategory"
                            value={values.businessCategory}
                            onChange={(e) => {
                              if (e.target.value === '__add_other__') {
                                handleAddOther();
                                return;
                              }
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            displayEmpty
                            IconComponent={KeyboardArrowDownIcon}
                            sx={selectStyles}
                            disabled={isCategoriesLoading}
                          >
                            <MenuItem value="" disabled>
                              <span style={{ color: colors.textTertiary }}>
                                {isCategoriesLoading ? 'Loading categories...' : 'Select a category'}
                              </span>
                            </MenuItem>
                            {categoryOptions.map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                            <MenuItem
                              value="__add_other__"
                              sx={{
                                borderTop: `1px solid ${colors.border}`,
                                mt: 1,
                                pt: 1.5,
                                color: colors.primary,
                                fontWeight: 500,
                              }}
                            >
                              <AddIcon sx={{ fontSize: 18, mr: 1 }} />
                              Add Other
                            </MenuItem>
                          </Select>
                          {getError('businessCategory') && (
                            <FormHelperText>{getError('businessCategory')}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Contact Information Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PhoneOutlinedIcon sx={{ color: colors.textPrimary, fontSize: 20 }} />
                    <Typography sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: 18 }}>
                      Contact Information
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Email */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Email Address <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="businessEmail"
                        value={values.businessEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="business@example.com"
                        error={!!getError('businessEmail')}
                        helperText={getError('businessEmail')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Box>

                    {/* Phone Number with Country Code */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Phone Number <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <FormControl sx={{ width: 120, flexShrink: 0 }}>
                          <Select
                            value={selectedPhoneCountry.dialCode}
                            onChange={(e) => handlePhoneCountryChange(e.target.value)}
                            IconComponent={KeyboardArrowDownIcon}
                            sx={countryCodeSelectStyles}
                            renderValue={() => (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ fontSize: 20 }}>{selectedPhoneCountry.flag}</span>
                                <span style={{ color: colors.gray700, fontSize: 14 }}>
                                  {selectedPhoneCountry.dialCode}
                                </span>
                              </Box>
                            )}
                            MenuProps={{
                              PaperProps: {
                                sx: { maxHeight: 300 },
                              },
                            }}
                          >
                            {COUNTRIES.map((country) => (
                              <MenuItem key={country.id} value={country.dialCode}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                  <span style={{ fontSize: 20 }}>{country.flag}</span>
                                  <span>{country.name}</span>
                                  <span style={{ color: colors.textSecondary, marginLeft: 'auto' }}>
                                    {country.dialCode}
                                  </span>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          fullWidth
                          name="phoneNumber"
                          value={values.phoneNumber}
                          onChange={(e) =>
                            setFieldValue('phoneNumber', e.target.value.replace(/\D/g, ''))
                          }
                          onBlur={handleBlur}
                          placeholder="Enter mobile number"
                          error={!!getError('phoneNumber')}
                          helperText={getError('phoneNumber')}
                          inputProps={{ maxLength: 12 }}
                          sx={inputStyles}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Website */}
                  <Box>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      Website (Optional)
                    </Typography>
                    <TextField
                      fullWidth
                      name="website"
                      value={values.website}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="https://www.example.com"
                      error={!!getError('website')}
                      helperText={getError('website')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LanguageOutlinedIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputStyles}
                    />
                  </Box>
                </Box>

                {/* Business Address Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOnOutlinedIcon sx={{ color: colors.textPrimary, fontSize: 20 }} />
                    <Typography sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: 18 }}>
                      Business Address
                    </Typography>
                  </Box>

                  {/* Street Address */}
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      Street Address <span style={{ color: colors.error }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      name="streetAddress"
                      value={values.streetAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter complete street address"
                      error={!!getError('streetAddress')}
                      helperText={getError('streetAddress')}
                      sx={inputStyles}
                    />
                  </Box>

                  {/* Country and State Row */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Country */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Country <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <FormControl fullWidth error={!!getError('country')} sx={formControlStyles}>
                        <Select
                          value={values.country}
                          onChange={(e) => handleCountryChange(Number(e.target.value))}
                          onBlur={handleBlur}
                          name="country"
                          displayEmpty
                          IconComponent={KeyboardArrowDownIcon}
                          sx={selectStyles}
                        >
                          <MenuItem value="" disabled>
                            <span style={{ color: colors.textTertiary }}>Select Country</span>
                          </MenuItem>
                          {COUNTRIES.map((country) => (
                            <MenuItem key={country.id} value={String(country.id)}>
                              {country.flag} {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {getError('country') && (
                          <FormHelperText>{getError('country')}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>

                    {/* State */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        State <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <FormControl fullWidth error={!!getError('state')} sx={formControlStyles}>
                        <Select
                          value={values.state}
                          onChange={(e) => handleStateChange(Number(e.target.value))}
                          onBlur={handleBlur}
                          name="state"
                          displayEmpty
                          IconComponent={KeyboardArrowDownIcon}
                          sx={selectStyles}
                          disabled={!values.country || availableStates.length === 0}
                        >
                          <MenuItem value="" disabled>
                            <span style={{ color: colors.textTertiary }}>
                              {!values.country ? 'Select country first' : 'Select State'}
                            </span>
                          </MenuItem>
                          {availableStates.map((state) => (
                            <MenuItem key={state.id} value={String(state.id)}>
                              {state.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {getError('state') && <FormHelperText>{getError('state')}</FormHelperText>}
                      </FormControl>
                    </Box>
                  </Box>

                  {/* City and PIN Code Row */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* City */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        City <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="city"
                        value={values.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter city"
                        error={!!getError('city')}
                        helperText={getError('city')}
                        sx={inputStyles}
                      />
                    </Box>

                    {/* PIN Code */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        PIN Code <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="postalCode"
                        value={values.postalCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="400001"
                        error={!!getError('postalCode')}
                        helperText={getError('postalCode')}
                        sx={inputStyles}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Billing Details Section */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptOutlinedIcon sx={{ color: colors.textPrimary, fontSize: 20 }} />
                      <Typography sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: 18 }}>
                        Billing Details
                      </Typography>
                    </Box>

                    <FormControlLabel
                      control={
                        <Checkbox
                          name="sameAsBusiness"
                          checked={values.sameAsBusiness}
                          onChange={handleChange}
                          sx={{
                            color: colors.textSecondary,
                            '&.Mui-checked': { color: colors.primary },
                          }}
                        />
                      }
                      label="Same as business details"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: 14, color: colors.gray700 } }}
                    />
                  </Box>

                  {/* Registered Business Name */}
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      Registered Business Name
                    </Typography>
                    <TextField
                      fullWidth
                      name="registeredBusinessName"
                      value={values.sameAsBusiness ? values.businessName : values.registeredBusinessName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter registered business name"
                      disabled={values.sameAsBusiness}
                      error={!values.sameAsBusiness && !!getError('registeredBusinessName')}
                      helperText={!values.sameAsBusiness ? getError('registeredBusinessName') : ''}
                      sx={inputStyles}
                    />
                  </Box>

                  {/* Billing Email and Phone Row */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Billing Email */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Billing Email <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="billingEmail"
                        value={values.sameAsBusiness ? values.businessEmail : values.billingEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="billing@example.com"
                        disabled={values.sameAsBusiness}
                        error={!values.sameAsBusiness && !!getError('billingEmail')}
                        helperText={!values.sameAsBusiness ? getError('billingEmail') : ''}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Box>

                    {/* Billing Phone Number with Country Code */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Billing Phone <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <FormControl sx={{ width: 120, flexShrink: 0 }}>
                          <Select
                            value={
                              values.sameAsBusiness
                                ? selectedPhoneCountry.dialCode
                                : selectedBillingPhoneCountry.dialCode
                            }
                            onChange={(e) => handleBillingPhoneCountryChange(e.target.value)}
                            disabled={values.sameAsBusiness}
                            IconComponent={KeyboardArrowDownIcon}
                            sx={countryCodeSelectStyles}
                            renderValue={() => (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ fontSize: 20 }}>
                                  {values.sameAsBusiness
                                    ? selectedPhoneCountry.flag
                                    : selectedBillingPhoneCountry.flag}
                                </span>
                                <span style={{ color: colors.gray700, fontSize: 14 }}>
                                  {values.sameAsBusiness
                                    ? selectedPhoneCountry.dialCode
                                    : selectedBillingPhoneCountry.dialCode}
                                </span>
                              </Box>
                            )}
                            MenuProps={{
                              PaperProps: {
                                sx: { maxHeight: 300 },
                              },
                            }}
                          >
                            {COUNTRIES.map((country) => (
                              <MenuItem key={country.id} value={country.dialCode}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                  <span style={{ fontSize: 20 }}>{country.flag}</span>
                                  <span>{country.name}</span>
                                  <span style={{ color: colors.textSecondary, marginLeft: 'auto' }}>
                                    {country.dialCode}
                                  </span>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          fullWidth
                          name="billingPhoneNumber"
                          value={
                            values.sameAsBusiness ? values.phoneNumber : values.billingPhoneNumber
                          }
                          onChange={(e) =>
                            setFieldValue('billingPhoneNumber', e.target.value.replace(/\D/g, ''))
                          }
                          onBlur={handleBlur}
                          placeholder="Enter mobile number"
                          disabled={values.sameAsBusiness}
                          error={!values.sameAsBusiness && !!getError('billingPhoneNumber')}
                          helperText={!values.sameAsBusiness ? getError('billingPhoneNumber') : ''}
                          inputProps={{ maxLength: 12 }}
                          sx={inputStyles}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Billing Street Address */}
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      Street Address <span style={{ color: colors.error }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      name="billingStreetAddress"
                      value={
                        values.sameAsBusiness ? values.streetAddress : values.billingStreetAddress
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter complete street address"
                      disabled={values.sameAsBusiness}
                      error={!values.sameAsBusiness && !!getError('billingStreetAddress')}
                      helperText={!values.sameAsBusiness ? getError('billingStreetAddress') : ''}
                      sx={inputStyles}
                    />
                  </Box>

                  {/* Billing Country and State Row */}
                  {!values.sameAsBusiness && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      {/* Billing Country */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}
                        >
                          Country <span style={{ color: colors.error }}>*</span>
                        </Typography>
                        <FormControl fullWidth error={!!getError('billingCountry')} sx={formControlStyles}>
                          <Select
                            value={values.billingCountry}
                            onChange={(e) => handleBillingCountryChange(Number(e.target.value))}
                            onBlur={handleBlur}
                            name="billingCountry"
                            displayEmpty
                            IconComponent={KeyboardArrowDownIcon}
                            sx={selectStyles}
                          >
                            <MenuItem value="" disabled>
                              <span style={{ color: colors.textTertiary }}>Select Country</span>
                            </MenuItem>
                            {COUNTRIES.map((country) => (
                              <MenuItem key={country.id} value={String(country.id)}>
                                {country.flag} {country.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {getError('billingCountry') && (
                            <FormHelperText>{getError('billingCountry')}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>

                      {/* Billing State */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}
                        >
                          State <span style={{ color: colors.error }}>*</span>
                        </Typography>
                        <FormControl fullWidth error={!!getError('billingState')} sx={formControlStyles}>
                          <Select
                            value={values.billingState}
                            onChange={(e) => handleBillingStateChange(Number(e.target.value))}
                            onBlur={handleBlur}
                            name="billingState"
                            displayEmpty
                            IconComponent={KeyboardArrowDownIcon}
                            sx={selectStyles}
                            disabled={!values.billingCountry || billingAvailableStates.length === 0}
                          >
                            <MenuItem value="" disabled>
                              <span style={{ color: colors.textTertiary }}>
                                {!values.billingCountry ? 'Select country first' : 'Select State'}
                              </span>
                            </MenuItem>
                            {billingAvailableStates.map((state) => (
                              <MenuItem key={state.id} value={String(state.id)}>
                                {state.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {getError('billingState') && (
                            <FormHelperText>{getError('billingState')}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    </Box>
                  )}

                  {/* Billing City and PIN Code Row */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Billing City */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        City <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="billingCity"
                        value={values.sameAsBusiness ? values.city : values.billingCity}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter city"
                        disabled={values.sameAsBusiness}
                        error={!values.sameAsBusiness && !!getError('billingCity')}
                        helperText={!values.sameAsBusiness ? getError('billingCity') : ''}
                        sx={inputStyles}
                      />
                    </Box>

                    {/* Billing PIN Code */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        PIN Code <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="billingPostalCode"
                        value={values.sameAsBusiness ? values.postalCode : values.billingPostalCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="400001"
                        disabled={values.sameAsBusiness}
                        error={!values.sameAsBusiness && !!getError('billingPostalCode')}
                        helperText={!values.sameAsBusiness ? getError('billingPostalCode') : ''}
                        sx={inputStyles}
                      />
                    </Box>
                  </Box>

                  {/* GST Number */}
                  <Box>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      GST Number <span style={{ color: colors.textSecondary, fontSize: '0.85em' }}>(Optional)</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="gstNumber"
                      value={values.gstNumber}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setFieldValue('gstNumber', value);
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        setFieldTouched('gstNumber', true, true);
                      }}
                      placeholder="Enter GST Number"
                      error={!!getError('gstNumber')}
                      helperText={getError('gstNumber')}
                      sx={inputStyles}
                    />
                  </Box>
                </Box>

                {/* Update Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  type="submit"
                  disabled={isSubmitting || isUpdating}
                  sx={{
                    height: 56,
                    borderRadius: '14px',
                    backgroundColor: colors.primary,
                    color: colors.textWhite,
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.1)',
                    fontWeight: 600,
                    fontSize: 16,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: colors.primaryDark,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: colors.gray200,
                      color: colors.textTertiary,
                    },
                  }}
                >
                  {isSubmitting || isUpdating ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Update Business'
                  )}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Shared input styles
const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: 'transparent',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.inputBorder,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '16px',
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: 'normal',
    height: 24,
    boxSizing: 'content-box',
    '&::placeholder': {
      color: colors.textTertiary,
      opacity: 1,
    },
  },
  '& .MuiOutlinedInput-multiline': {
    padding: 0,
    '& .MuiOutlinedInput-input': {
      padding: '12px 16px',
      height: 'auto',
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginTop: '4px',
    marginBottom: 0,
    fontSize: 12,
  },
};

const selectStyles = {
  height: 56,
  borderRadius: '10px',
  backgroundColor: colors.backgroundPaper,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E2E8F0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.primary,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.primary,
  },
  '& .MuiSelect-select': {
    fontSize: 14,
  },
};

const formControlStyles = {
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginTop: '4px',
    fontSize: 12,
  },
};

const countryCodeSelectStyles = {
  height: 56,
  width: '100%',
  backgroundColor: colors.backgroundPaper,
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E2E8F0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.primary,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.primary,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    paddingRight: '32px !important',
  },
  '& .MuiSelect-icon': {
    right: 8,
  },
};

export default EditBusinessScreen;
